import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CalendarDays,
  ChevronRight,
  CircleAlert,
  Crown,
  Loader2,
  PackageCheck,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  WalletCards,
  X,
} from 'lucide-react';
import { createBillingRefundRequest, getBillingPackages } from './billingPackagesApi';
import { formatBillingDate } from './billingData';

const STATUS_META = {
  pending_review: { zh: '待审核', en: 'Pending review', tone: 'warning' },
  approved: { zh: '审核通过', en: 'Approved', tone: 'pending' },
  rejected: { zh: '已驳回', en: 'Rejected', tone: 'quiet' },
  invoice_red_processing: { zh: '发票红冲处理中', en: 'Invoice red-flush processing', tone: 'warning' },
  refund_processing: { zh: '退款处理中', en: 'Refund processing', tone: 'warning' },
  refunded: { zh: '已退款', en: 'Refunded', tone: 'success' },
  refund_failed: { zh: '退款失败', en: '退款失败', tone: 'danger' },
  manual_review: { zh: '人工核对中', en: 'Manual review', tone: 'danger' },
};

function createIdempotencyKey() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
  const values = new Uint8Array(16);
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) window.crypto.getRandomValues(values);
  else values.forEach((_, index) => { values[index] = Math.floor(Math.random() * 256); });
  values[6] = (values[6] & 0x0f) | 0x40;
  values[8] = (values[8] & 0x3f) | 0x80;
  const hex = Array.from(values, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function money(value) {
  return Number(value || 0).toFixed(2);
}

function credits(value, english) {
  return Number(value || 0).toLocaleString(english ? 'en-US' : 'zh-CN');
}

function statusMeta(summary, english) {
  const meta = STATUS_META[summary?.status] || { zh: summary?.status_label || '状态确认中', en: summary?.status_label || 'Status pending', tone: 'pending' };
  return { label: english ? meta.en : meta.zh, tone: meta.tone };
}

function DialogPortal({ children }) {
  if (typeof document === 'undefined') return null;
  return createPortal(<div className="my-packages-dialog-portal">{children}</div>, document.body);
}

function PackageDetailDialog({ item, english, onClose, onRequestRefund }) {
  const summary = item.refund_request_summary;
  const eligibility = item.refund_eligibility || {};
  const meta = statusMeta(summary, english);
  const rejected = summary?.review_status === 'rejected' || summary?.status === 'rejected';
  const rejectionReason = summary?.review_remark || summary?.message;
  const needsReview = ['invoice_red_processing', 'refund_failed', 'manual_review'].includes(summary?.status);
  const progressMessage = needsReview
    ? getErrorMessage(summary?.message, '退款流程尚未完成，请联系管理员核对，请勿重复申请。')
    : summary?.message;

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="my-packages-dialog-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="my-packages-dialog my-packages-detail-dialog" role="dialog" aria-modal="true" aria-labelledby="package-detail-title">
        <header>
          <div>
            <ReceiptText size={17} />
            <h2 id="package-detail-title">{english ? 'Order details' : '订单详情'}</h2>
          </div>
          <button type="button" aria-label={english ? 'Close' : '关闭'} onClick={onClose}><X size={16} /></button>
        </header>
        <div className="my-packages-dialog__body">
          <div className="my-packages-dialog__summary">
            <span><small>{english ? 'Order' : '订单号'}</small><strong>{item.order_no}</strong></span>
            <span><small>{english ? 'Package' : '购买套餐'}</small><strong>{item.package_name} · {item.tier_name} × {item.quantity}</strong></span>
            <span><small>{english ? 'Paid / channel' : '实付 / 渠道'}</small><strong>¥{money(item.paid_amount)} · {item.payment_channel_label}</strong></span>
            <span><small>{english ? 'Credits' : '剩余 / 原始点数'}</small><strong>{credits(item.remaining_credits, english)} / {credits(item.original_credits, english)}</strong></span>
            <span><small>{english ? 'Purchased' : '支付时间'}</small><strong>{formatBillingDate(item.paid_at || item.created_at, { locale: english ? 'en-US' : 'zh-CN' })}</strong></span>
            <span><small>{english ? 'Expires' : '过期时间'}</small><strong>{formatBillingDate(item.expires_at, { locale: english ? 'en-US' : 'zh-CN' })}</strong></span>
          </div>

          <section className="my-packages-detail-support" aria-label={english ? 'Refunds and support' : '退款与售后'}>
            <div>
              <strong>{english ? 'Refunds and support' : '退款与售后'}</strong>
              {rejected ? (
                <div className="my-packages-detail-rejection" role="status">
                  <small>{english ? 'Rejection reason' : '驳回理由'}</small>
                  <p>{rejectionReason || (english ? 'No reason was provided.' : '暂无驳回理由')}</p>
                </div>
              ) : (
                <p>{progressMessage || eligibility.message || (english
                  ? 'Eligible unused WeChat or Alipay purchases can be submitted for administrator review.'
                  : '符合条件且未使用的微信或支付宝点数订单，可提交后台审核。')}</p>
              )}
            </div>
            {summary ? (
              <span className={`my-packages-status is-${meta.tone}`}><i />{meta.label}</span>
            ) : eligibility.eligible ? (
              <button type="button" className="my-packages-detail-refund-link" onClick={() => onRequestRefund(item)}>
                {english ? 'Request refund' : '申请退款'}<ChevronRight size={13} />
              </button>
            ) : (
              <span className="my-packages-detail-unavailable">{'当前不可申请'}</span>
            )}
          </section>
        </div>
        <footer>
          <button type="button" className="my-packages-button is-quiet" onClick={onClose}>{english ? 'Close' : '关闭'}</button>
        </footer>
      </section>
    </div>
  );
}

function RefundDialog({ item, english, busy, error, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 2;
  const invoiceRedRequired = Boolean(item.refund_eligibility?.invoice_red_required);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel]);

  return (
    <div className="my-packages-dialog-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget && !busy) onCancel();
    }}>
      <section className="my-packages-dialog" role="dialog" aria-modal="true" aria-labelledby="refund-dialog-title">
        <header>
          <div>
            <RotateCcw size={17} />
            <h2 id="refund-dialog-title">{english ? 'Request a refund' : '申请退款'}</h2>
          </div>
          <button type="button" aria-label={english ? 'Close' : '关闭'} disabled={busy} onClick={onCancel}><X size={16} /></button>
        </header>
        <div className="my-packages-dialog__body">
          <div className="my-packages-dialog__summary">
            <span><small>{english ? 'Order' : '订单'}</small><strong>{item.order_no}</strong></span>
            <span><small>{english ? 'Credits frozen now' : '立即冻结点数'}</small><strong>{credits(item.original_credits, english)}</strong></span>
            <span><small>{english ? 'Paid' : '实付金额'}</small><strong>¥{money(item.paid_amount)}</strong></span>
            <span><small>{english ? 'Expires' : '原过期时间'}</small><strong>{formatBillingDate(item.expires_at, { locale: english ? 'en-US' : 'zh-CN' })}</strong></span>
          </div>
          <div className="my-packages-dialog__warning">
            <CircleAlert size={16} />
            <p>{invoiceRedRequired
              ? (english
                ? 'Submitting freezes every remaining credit immediately. Because this order has an issued invoice, approval requires a full refund: the invoice is red-flushed first, then the payment is returned through the original channel.'
                : '提交后整笔订单点数立即冻结。该订单已开票，审核时只能整单退款：先完成发票红冲，再按原支付渠道退款。')
              : (english
                ? 'Submitting freezes every remaining credit from this order immediately. The administrator determines the approved quantity; the amount and credits are calculated from the original order snapshot.'
                : '提交后整笔订单点数立即冻结。后台仅选择批准退款份数，退款金额和扣回点数由原订单快照自动计算。')}</p>
          </div>
          <label htmlFor="refund-reason">{english ? 'Reason for refund' : '退款原因'}</label>
          <textarea
            id="refund-reason"
            autoFocus
            maxLength={500}
            rows={4}
            value={reason}
            disabled={busy}
            placeholder={english ? 'Describe the reason (2–500 characters)' : '请说明退款原因（2–500 个字符）'}
            onChange={event => setReason(event.target.value)}
          />
          <small className="my-packages-dialog__count">{reason.length}/500</small>
          {error && <p className="my-packages-dialog__error" role="alert">{error}</p>}
        </div>
        <footer>
          <button type="button" className="my-packages-button is-quiet" disabled={busy} onClick={onCancel}>{english ? 'Cancel' : '取消'}</button>
          <button type="button" className="my-packages-button is-danger" disabled={busy || !valid} onClick={() => onConfirm(reason.trim())}>
            {busy && <Loader2 size={14} className="my-packages-spin" />}
            {busy ? (english ? 'Submitting…' : '提交中…') : (english ? 'Freeze credits and submit' : '冻结点数并提交')}
          </button>
        </footer>
      </section>
    </div>
  );
}

export default function MyPackagesPanel({ preview = false, english = false, embedded = false }) {
  const [scope, setScope] = useState('personal');
  const [data, setData] = useState({ subscription: null, point_purchases: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState({ tone: '', message: '' });
  const [detailTarget, setDetailTarget] = useState(null);
  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (preview) {
        setData({ subscription: null, point_purchases: [], scope, team_id: null, team_name: null });
        return;
      }
      setData(await getBillingPackages(scope));
    } catch (requestError) {
      setError(getErrorMessage(requestError, '我的套餐暂时无法加载'));
    } finally {
      setLoading(false);
    }
  }, [preview, scope]);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => Array.isArray(data?.point_purchases) ? data.point_purchases : [], [data]);

  const requestRefund = async reason => {
    if (!target || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await createBillingRefundRequest({
        order_id: target.order_id,
        reason,
        idempotency_key: createIdempotencyKey(),
      });
      const invoiceRedRequired = Boolean(target.refund_eligibility?.invoice_red_required);
      setNotice({
        tone: 'success',
        message: invoiceRedRequired
          ? (english
            ? 'Refund request submitted and credits frozen. After approval, the invoice will be red-flushed before the original-channel refund.'
            : '退款申请已提交，整笔订单点数已冻结。审核通过后将先红冲发票，再原路退款。')
          : (english
            ? 'Refund request submitted. All credits from this order are frozen pending review.'
            : '退款申请已提交，整笔订单点数已冻结并等待后台审核。'),
      });
      setTarget(null);
      await load();
    } catch (requestError) {
      const rateLimited = Number(requestError?.code) === 429
        || requestError?.response?.status === 429
        || /too many (attempts|requests)/i.test(requestError?.message || '');
      setSubmitError(getErrorMessage(rateLimited
        ? ('退款申请提交过于频繁，请稍后再试。')
        : requestError?.message || ('退款申请提交失败'), '退款申请提交失败'));
    } finally {
      setSubmitting(false);
    }
  };

  const scopeSwitcher = (
    <div className="billing-segmented" role="tablist" aria-label={english ? 'Package scope' : '套餐归属'}>
      <button type="button" role="tab" aria-selected={scope === 'personal'} className={scope === 'personal' ? 'is-active' : ''} onClick={() => setScope('personal')}>{english ? 'Personal' : '个人'}</button>
      <button type="button" role="tab" aria-selected={scope === 'team'} className={scope === 'team' ? 'is-active' : ''} onClick={() => setScope('team')}>{english ? 'Current team' : '当前团队'}</button>
    </div>
  );

  return (
    <div className={embedded ? 'my-packages my-packages--embedded' : 'account-content__stack my-packages'}>
      {embedded ? (
        <section className="my-packages-embedded-heading">
          <div>
            <span className="my-packages-embedded-heading__icon"><PackageCheck size={17} /></span>
            <span>
              <strong>{english ? 'My Packages' : '我的套餐'}</strong>
              <small>{english ? 'Active subscriptions and one-time credit purchases' : '有效订阅与点数直购，订阅套餐不可退款'}</small>
            </span>
          </div>
          {scopeSwitcher}
        </section>
      ) : (
        <section className="account-page-heading my-packages__heading">
          <div>
            <span className="account-eyebrow">PACKAGES</span>
            <h1>{english ? 'My Packages' : '我的套餐'}</h1>
            <p>{english ? 'Review active subscriptions, one-time credit purchases, and order status.' : '查看有效订阅、点数直购与订单状态。'}</p>
          </div>
          {scopeSwitcher}
        </section>
      )}

      {notice.message && <div className={`my-packages-notice is-${notice.tone}`} role="status"><ShieldCheck size={16} /><span>{notice.message}</span><button type="button" aria-label={english ? 'Dismiss' : '关闭提示'} onClick={() => setNotice({ tone: '', message: '' })}><X size={14} /></button></div>}

      {loading ? (
        <section className="my-packages-state"><Loader2 className="my-packages-spin" size={20} /><strong>{english ? 'Loading your packages…' : '正在加载我的套餐…'}</strong></section>
      ) : error ? (
        <section className="my-packages-state is-error"><CircleAlert size={20} /><strong>{error}</strong><button type="button" className="my-packages-button is-quiet" onClick={load}><RefreshCw size={14} />{english ? 'Try again' : '重试'}</button></section>
      ) : (
        <>
          <section className="my-packages-section">
            <header><div><Crown size={17} /><span><h2>{english ? 'Current subscription' : '当前订阅'}</h2><p>{scope === 'team' && data?.team_name ? data.team_name : (english ? 'Current effective plan' : '当前生效中的套餐')}</p></span></div><em>{english ? 'Non-refundable' : '不可退款'}</em></header>
            {data?.subscription ? (
              <article className="my-packages-subscription">
                <span className="my-packages-subscription__icon"><PackageCheck size={21} /></span>
                <div><strong>{data.subscription.plan_name}</strong><small>{data.subscription.billing_cycle || (english ? 'Subscription' : '订阅套餐')}</small></div>
                <span><small>{english ? 'Started' : '开始时间'}</small><strong>{formatBillingDate(data.subscription.starts_at, { locale: english ? 'en-US' : 'zh-CN' })}</strong></span>
                <span><small>{english ? 'Expires' : '到期时间'}</small><strong>{formatBillingDate(data.subscription.expires_at, { locale: english ? 'en-US' : 'zh-CN' })}</strong></span>
                <b>{english ? 'Subscription plans cannot be refunded' : '订阅套餐不可退款'}</b>
              </article>
            ) : (
              <div className="my-packages-empty"><Crown size={20} /><strong>{english ? 'No active subscription' : '暂无有效订阅'}</strong><span>{english ? 'Purchased subscriptions will appear here.' : '购买并生效的订阅会显示在这里。'}</span></div>
            )}
          </section>

          <section className="my-packages-section">
            <header><div><WalletCards size={17} /><span><h2>{english ? 'One-time credit purchases' : '点数直购'}</h2><p>{english ? 'Review active credits and the order they came from.' : '核对当前有效点数和对应订单。'}</p></span></div><em>{items.length} {english ? 'active' : '笔有效'}</em></header>
            {items.length ? (
              <div className="my-packages-list">
                {items.map(item => {
                  return (
                    <article className="my-packages-row" key={item.order_id}>
                      <div className="my-packages-row__product"><strong>{item.package_name} · {item.tier_name}</strong><span>{english ? `${item.quantity} pack(s)` : `购买 ${item.quantity} 份`} · {item.order_no}</span></div>
                      <div className="my-packages-row__metric"><small>{english ? 'Credits' : '点数'}</small><strong>{credits(item.remaining_credits, english)} / {credits(item.original_credits, english)}</strong></div>
                      <div className="my-packages-row__metric"><small>{english ? 'Paid / channel' : '实付 / 渠道'}</small><strong>¥{money(item.paid_amount)} · {item.payment_channel_label}</strong></div>
                      <div className="my-packages-row__metric"><small>{'过期时间'}</small><strong><CalendarDays size={12} />{formatBillingDate(item.expires_at, { locale: english ? 'en-US' : 'zh-CN' })}</strong></div>
                      <div className="my-packages-row__action">
                        <div className="my-packages-row__action-main">
                          <button type="button" className="my-packages-detail-button" onClick={() => setDetailTarget(item)}>
                            {english ? 'Order details' : '订单详情'}<ChevronRight size={13} />
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="my-packages-empty"><WalletCards size={20} /><strong>{english ? 'No active credit purchases' : '暂无有效点数直购'}</strong><span>{english ? 'Expired and fully refunded records remain in Billing History.' : '已过期和已全额退款记录可前往账单记录查看。'}</span></div>
            )}
          </section>
        </>
      )}

      {detailTarget && <DialogPortal><PackageDetailDialog
        item={detailTarget}
        english={english}
        onClose={() => setDetailTarget(null)}
        onRequestRefund={item => {
          setDetailTarget(null);
          setSubmitError('');
          setTarget(item);
        }}
      /></DialogPortal>}
      {target && <DialogPortal><RefundDialog item={target} english={english} busy={submitting} error={submitError} onCancel={() => { if (!submitting) setTarget(null); }} onConfirm={requestRefund} /></DialogPortal>}
    </div>
  );
}
